import { useState, useEffect, useCallback } from "react";
import { ChevronRight, ChevronLeft, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const GUIDE_STEPS = [
  {
    target: "[data-testid='main-textarea']",
    title: "Área de Texto",
    description: "Aqui você escreve, cola ou dita seu texto. Tudo começa aqui — suas ideias ganham forma neste espaço.",
    position: "bottom",
  },
  {
    target: "[data-testid='mic-button']",
    title: "Gravar por Voz",
    description: "Clique no microfone para gravar sua voz. O que você falar será convertido em texto automaticamente.",
    position: "top",
  },
  {
    target: "[data-testid='play-button']",
    title: "Ouvir Texto",
    description: "Ouça seu texto em voz alta. A palavra sendo lida é destacada em tempo real para você acompanhar.",
    position: "top",
  },
  {
    target: "[data-testid='save-button']",
    title: "Salvar Ideia",
    description: "Salve o texto como uma ideia com título e categoria. Suas ideias ficam organizadas e sincronizadas.",
    position: "top",
  },
  {
    target: "[data-testid='open-ideas-button'], [data-testid='layout-toggle-button']",
    title: "Minhas Ideias",
    description: "Acesse todas as suas ideias salvas. Busque por título, conteúdo ou categoria. Carregue uma ideia para editar ou ouvir.",
    position: "bottom",
  },
  {
    target: "[data-testid='open-shares-button']",
    title: "Links Compartilhados",
    description: "Veja todos os links que você compartilhou. Controle quem pode ver, salvar ou revogar acesso a suas ideias.",
    position: "bottom",
  },
  {
    target: "[data-testid='open-messages-button']",
    title: "Mensagens",
    description: "Receba feedback e converse sobre ideias compartilhadas. Colabore diretamente com outros usuários.",
    position: "bottom",
  },
  {
    target: "[data-testid='focus-mode-button']",
    title: "Modo Foco",
    description: "Ative o modo foco para escrever sem distrações. Tela limpa, apenas você e suas ideias.",
    position: "bottom",
  },
];

function getElementRect(selector) {
  const selectors = selector.split(",").map((s) => s.trim());
  for (const s of selectors) {
    const el = document.querySelector(s);
    if (el) {
      const rect = el.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) return rect;
    }
  }
  return null;
}

function TooltipBox({ step, rect, current, total, onNext, onPrev, onClose }) {
  const padding = 12;
  const tooltipWidth = 320;
  let top, left;

  if (!rect) {
    top = "50%";
    left = "50%";
    return (
      <div
        className="fixed z-[10002] w-80 animate-fade-in-up"
        style={{ top, left, transform: "translate(-50%, -50%)" }}
      >
        <InnerContent step={step} current={current} total={total} onNext={onNext} onPrev={onPrev} onClose={onClose} />
      </div>
    );
  }

  if (step.position === "bottom") {
    top = rect.bottom + padding;
    left = Math.max(16, Math.min(rect.left + rect.width / 2 - tooltipWidth / 2, window.innerWidth - tooltipWidth - 16));
  } else {
    top = rect.top - padding;
    left = Math.max(16, Math.min(rect.left + rect.width / 2 - tooltipWidth / 2, window.innerWidth - tooltipWidth - 16));
  }

  return (
    <div
      className="fixed z-[10002] w-80 animate-fade-in-up"
      style={{
        top: step.position === "bottom" ? `${top}px` : undefined,
        bottom: step.position === "top" ? `${window.innerHeight - top}px` : undefined,
        left: `${left}px`,
      }}
    >
      <InnerContent step={step} current={current} total={total} onNext={onNext} onPrev={onPrev} onClose={onClose} />
    </div>
  );
}

function InnerContent({ step, current, total, onNext, onPrev, onClose }) {
  const isLast = current === total - 1;
  return (
    <div className="bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
      <div className="px-5 pt-5 pb-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[11px] font-medium text-indigo-500 uppercase tracking-wide">
            Passo {current + 1} de {total}
          </span>
          <button
            onClick={onClose}
            className="w-6 h-6 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-400 transition-colors"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
        <h3 className="text-base font-bold text-gray-900 mb-1.5">{step.title}</h3>
        <p className="text-sm text-gray-500 leading-relaxed">{step.description}</p>
      </div>
      <div className="px-5 pb-4 flex items-center justify-between">
        <div className="flex gap-1">
          {Array.from({ length: total }).map((_, i) => (
            <div
              key={i}
              className={`w-1.5 h-1.5 rounded-full transition-all ${
                i === current ? "w-4 bg-indigo-500" : i < current ? "bg-indigo-200" : "bg-gray-200"
              }`}
            />
          ))}
        </div>
        <div className="flex gap-2">
          {current > 0 && (
            <Button variant="ghost" size="sm" onClick={onPrev} className="h-8 text-xs text-gray-500">
              <ChevronLeft className="w-3.5 h-3.5 mr-0.5" />
              Anterior
            </Button>
          )}
          <Button
            size="sm"
            onClick={isLast ? onClose : onNext}
            className="h-8 text-xs bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            {isLast ? "Concluir" : "Próximo"}
            {!isLast && <ChevronRight className="w-3.5 h-3.5 ml-0.5" />}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function InteractiveGuide({ onClose }) {
  const [current, setCurrent] = useState(0);
  const [rect, setRect] = useState(null);
  const step = GUIDE_STEPS[current];

  const updateRect = useCallback(() => {
    if (!step) return;
    const r = getElementRect(step.target);
    setRect(r);
  }, [step]);

  useEffect(() => {
    updateRect();
    window.addEventListener("resize", updateRect);
    window.addEventListener("scroll", updateRect, true);
    return () => {
      window.removeEventListener("resize", updateRect);
      window.removeEventListener("scroll", updateRect, true);
    };
  }, [updateRect]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight" && current < GUIDE_STEPS.length - 1) setCurrent((c) => c + 1);
      if (e.key === "ArrowLeft" && current > 0) setCurrent((c) => c - 1);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [current, onClose]);

  const pad = 6;

  return (
    <div className="fixed inset-0 z-[10000]" data-testid="interactive-guide">
      {/* Overlay with cutout */}
      <svg className="fixed inset-0 w-full h-full z-[10001]" style={{ pointerEvents: "auto" }}>
        <defs>
          <mask id="guide-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {rect && (
              <rect
                x={rect.left - pad}
                y={rect.top - pad}
                width={rect.width + pad * 2}
                height={rect.height + pad * 2}
                rx="12"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          x="0" y="0" width="100%" height="100%"
          fill="rgba(0,0,0,0.55)"
          mask="url(#guide-mask)"
          onClick={onClose}
        />
      </svg>

      {/* Highlight ring */}
      {rect && (
        <div
          className="fixed z-[10001] rounded-xl pointer-events-none"
          style={{
            left: rect.left - pad,
            top: rect.top - pad,
            width: rect.width + pad * 2,
            height: rect.height + pad * 2,
            boxShadow: "0 0 0 3px rgba(99, 102, 241, 0.5), 0 0 20px rgba(99, 102, 241, 0.15)",
          }}
        />
      )}

      {/* Tooltip */}
      <TooltipBox
        step={step}
        rect={rect}
        current={current}
        total={GUIDE_STEPS.length}
        onNext={() => setCurrent((c) => c + 1)}
        onPrev={() => setCurrent((c) => c - 1)}
        onClose={onClose}
      />
    </div>
  );
}
